import { extend, localize } from 'vee-validate'
import {
  required as rule_required,
  required_if as rule_required_if,
  email as rule_email,
  min as rule_min,
  max as rule_max,
  confirmed as rule_confirmed,
  regex as rule_regex,
  between as rule_between,
  alpha as rule_alpha,
  integer as rule_integer,
  digits as rule_digits,
  alpha_dash as rule_alpha_dash,
  alpha_num as rule_alpha_num,
  length as rule_length,
} from 'vee-validate/dist/rules'
import ar from 'vee-validate/dist/locale/ar.json'
import en from 'vee-validate/dist/locale/en.json'
import settingsService from '@/services/settingsService'

// eslint-disable-next-line object-curly-newline
import { validatorPositive, validatorUrlValidator, validatorPassword, validatorCreditCard } from './validators'

// ////////////////////////////////////////////////////////
// General
// ////////////////////////////////////////////////////////

export const required = extend('required', rule_required)
export const required_if = extend('required_if', rule_required_if)

export const email = extend('email', rule_email)

export const min = extend('min', rule_min)
export const max = extend('max', rule_max)

// export const confirmed = extend('confirmed', rule_confirmed)
export const confirmed = extend('confirmed', {
  ...rule_confirmed,
  message: 'Поле "Повторите пароль" не совпадает с полем "Новый пароль"',
})

export const regex = extend('regex', rule_regex)

export const between = extend('between', rule_between)

export const alpha = extend('alpha', rule_alpha)

export const integer = extend('integer', rule_integer)

export const digits = extend('digits', rule_digits)

export const alphaDash = extend('alpha-dash', rule_alpha_dash)

export const alphaNum = extend('alpha-num', rule_alpha_num)

export const length = extend('length', rule_length)

export const positive = extend('positive', {
  validate: validatorPositive,
  message: 'Please enter positive number!',
})

export const credit = extend('credit-card', {
  validate: validatorCreditCard,
  message: 'It is not valid credit card!',
})

export const password = extend('password', value => {
  const minLength = settingsService.getSettings('common.password_min_length')
  const maxLength = settingsService.getSettings('common.password_max_length')
  if (validatorPassword(value, minLength, maxLength)) {
    return true
  }
  const string = maxLength ? `не менее ${minLength} и не более ${maxLength} символов` : `не менее ${minLength} символов`
  return `Поле "{_field_}" должно быть ${string} и может содержать латинские буквы, цифры или спецсимволы.`
})

export const url = extend('url', {
  validate: validatorUrlValidator,
  message: 'URL is invalid',
})

// Валидация на поле наименование банка
export const bankName = extend('bankName', value => {
  if (!/^.{1,34}$/.test(value)) {
    return 'Наименование банка должно быть от 1 до 34 символов.'
  }
  return true
})

// Валидация на поле бик
export const bic = extend('bic', value => {
  if (!/^[0-9]{9}$/.test(value)) {
    return 'БИК должен состоять из 9 цифр.'
  }
  return true
})

// Контрольная сумма для корреспондентского счета
const checkDigitCorrespondentAccount = (correspondentAccount, bic, coefficients = []) => {
  let sum = 0
  const digit = `0${bic.toString().slice(4, 6)}${correspondentAccount}`
  coefficients.forEach((coefficient, index) => {
    sum += coefficient * (+digit[index] % 10)
  })
  return (sum % 10) === 0
}

// Валидация на поле корреспондентский счет
export const correspondentAccount = extend('correspondentAccount', (value, [bic]) => {
  if (!/^[0-9]{20}$/.test(value)) {
    return 'Корреспондентский счет должен состоять из 20 цифр.'
  }
  if (bic) {
    const coefficients = [7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1]
    if (!checkDigitCorrespondentAccount(value, bic, coefficients)) {
      return 'Неверная контрольная сумма корреспондентского счета. Проверьте корреспондентский счет или БИК.'
    }
  }
  return true
})

// Валидация на поле наименование компании
export const companyName = extend('companyName', value => {
  if (!/^.{1,160}$/.test(value)) {
    return 'Наименование компании должно быть от 1 до 160 символов.'
  }
  return true
})

// Валидация на поле расчетный счет
export const personalAccount = extend('personalAccount', value => {
  if (!/^[0-9]{20}$/.test(value)) {
    return 'Расчетный счет должен состоять из 20 цифр.'
  }
  return true
})

// Контрольная сумма ИНН
const checkDigitInn = (inn, coefficients = []) => {
  let sum = 0
  coefficients.forEach((coefficient, index) => {
    sum += coefficient * inn[index]
  })
  return +(sum % 11 % 10)
}

// Валидация на поле ИНН
export const inn = extend('inn', value => {
  if (!/^[0-9]{10}$|^[0-9]{12}$/.test(value)) {
    return 'ИНН должен состоять для физических лиц из 12 цифр, а для юридических — из 10 цифр.'
  }

  if (/^[0-9]{10}$/.test(value)) {
    const checkDigit9 = checkDigitInn(value, [2, 4, 10, 3, 5, 9, 4, 6, 8])
    if (checkDigit9 !== +(value[9])) {
      return 'Контрольная сумма ИНН для юридических лиц неверна.'
    }
  }

  if (/^[0-9]{12}$/.test(value)) {
    const checkDigit10 = checkDigitInn(value, [7, 2, 4, 10, 3, 5, 9, 4, 6, 8])
    const checkDigit11 = checkDigitInn(value, [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8])
    if (checkDigit10 !== +(value[10]) || checkDigit11 !== +(value[11])) {
      return 'Контрольная сумма ИНН для физических лиц неверна.'
    }
  }

  return true
})

// Валидация на поле КПП
export const kpp = extend('kpp', value => {
  if (!/^[0-9]{0}$|^[0-9]{9}$/.test(value)) {
    return 'КПП должен состоять из 9 цифр.'
  }
  // if (/^[0-9A-Z]{9}$/.test(value) && !/^[0-9]{4}[0-9A-Z]{2}[0-9]{3}$/.test(value)) {
  //   return 'Неправильный формат КПП.'
  // }
  return true
})

// Валидация на поле ОГРН
export const ogrn = extend('ogrn', value => {
  if (!/^[0-9]{0}$|^[0-9]{13}$|^[0-9]{15}$/.test(value)) {
    return 'ОГРН/ОГРНИП должен состоять из 13 цифр для юридических лиц или 15 цифр для индивидуальных предпринимателей.'
  }
  return true
})

// Валидация на поле валюта
export const currency = extend('currency', value => {
  if (!/^[0-9|A-Z]{3}$/.test(value)) {
    return 'Валюта должна состоять из 3 цифр или 3 латинских букв верхнего регистра.'
  }
  return true
})

// Валидация на поле СНИЛС
export const snils = extend('snils', value => {
  if (!value) return true

  // Очищаем от маски (удаляем всё, кроме цифр)
  const snils = value.replace(/\D/g, '')

  if (snils.length !== 11) {
    return 'СНИЛС должен состоять из 11 цифр.'
  }

  // проверки контрольного числа
  let sum = 0
  for (let i = 0; i < 9; i += 1) {
    sum += parseInt(snils[i], 10) * (9 - i)
  }

  let checkDigit = 0
  if (sum < 100) {
    checkDigit = sum
  } else if (sum === 100 || sum === 101) {
    checkDigit = 0
  } else {
    checkDigit = sum % 101
    if (checkDigit === 100 || checkDigit === 101) checkDigit = 0
  }

  if (checkDigit !== parseInt(snils.slice(-2), 10)) {
    return 'Неверная контрольная сумма СНИЛС.'
  }

  return true
})

// Install English and Arabic localizations.
localize({
  en: {
    messages: en.messages,
    names: {
      email: 'Email',
      password: 'Password',
    },
    fields: {
      password: {
        min: '{_field_} is too short, you want to get hacked?',
      },
    },
  },
  ar: {
    messages: ar.messages,
    names: {
      email: 'البريد الإلكتروني',
      password: 'كلمة السر',
    },
    fields: {
      password: {
        min: 'كلمة السر قصيرة جداً سيتم اختراقك',
      },
    },
  },
})
// ////////////////////////////////////////////////////////
// NOTE:
// Quasar validation for reference only
// Remove this note once development is finished and make sure to
// to convert all of them in veevalidate version
// ////////////////////////////////////////////////////////

// export const required = (val) => {
//   return (val && val.length > 0) || '*Field is required'
// }

// export const required_obj = (obj) => {
//   if (obj === null || obj === undefined) return '*Field is required'
//   return (Object.entries(obj).length > 0 && obj.constructor === Object) || '*Field is required'
// }

// export const no_blank_spaces_arr = (arr) => {
//   return arr.every(val => (val.trim() && val.trim().length > 0)) || 'Blank Spaces are not allowed'
// }

// export const url = val => {
//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // Used
//   // https://stackoverflow.com/questions/4314741/url-regex-validation

//   // Other
//   // https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
//   // https://www.w3resource.com/javascript-exercises/javascript-regexp-exercise-9.php
//   // https://www.geeksforgeeks.org/how-to-validate-url-using-regular-expression-in-javascript/

//   /* eslint-disable no-useless-escape */
//   const re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/
//   /* eslint-enable no-useless-escape */
//   return re.test(val) || 'URL is invalid'
// }

// export const date = val => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // https://github.com/quasarframework/quasar/blob/dev/ui/src/utils/patterns.js
//   return /^-?[\d]+\/[0-1]\d\/[0-3]\d$/.test(val) || 'Date is invalid'
// }

// export const max = (val, max) => {

//   // If blank return
//   if (val === undefined || val === null) return true

//   return val.length <= max || `More than ${max} characters are not allowed`
// }

// export const max_arr = (val, max) => {
//   return val.length <= max || `More than ${max} values are not allowed`
// }

// export const min = (val, min) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   return val.length >= min || `Minimum ${min} characters are required`
// }

// export const num_range = (val, min, max) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   const msg = 'Value is invalid'
//   if (min === null) return val <= max || msg
//   else if (max === null) return val >= min || msg
//   else return (val >= min && val <= max) || msg
// }
